import { useEffect } from 'react'

export type SidebarListKeyboardHandlers<T extends { id: string }> = {
  items: T[]
  focusedId: string | null
  setFocusedId: (id: string | null) => void
  onOpen: (item: T, event?: { metaKey?: boolean; ctrlKey?: boolean }) => void
  onArchive?: (item: T) => void
  onDelete?: (item: T) => void
  /** When false, ignore keys (e.g. while renaming). */
  enabled?: boolean
  /** Root element that must contain focus for j/k to apply. */
  containerRef?: React.RefObject<HTMLElement | null>
}

/**
 * Shared sidebar list keyboard: j/k move focus, Enter open, Cmd+Enter new tab,
 * e / Delete archive or delete when handlers provided.
 */
export function useSidebarListKeyboard<T extends { id: string }>({
  items,
  focusedId,
  setFocusedId,
  onOpen,
  onArchive,
  onDelete,
  enabled = true,
  containerRef,
}: SidebarListKeyboardHandlers<T>) {
  useEffect(() => {
    if (!enabled || items.length === 0) return

    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return
      }

      if (containerRef?.current) {
        const active = document.activeElement
        if (active && !containerRef.current.contains(active)) {
          return
        }
      }

      const key = event.key.toLowerCase()
      const idx = focusedId ? items.findIndex((i) => i.id === focusedId) : -1

      if (key === 'j') {
        event.preventDefault()
        const next =
          idx < 0 ? 0 : Math.min(items.length - 1, idx + 1)
        setFocusedId(items[next]?.id ?? null)
        return
      }

      if (key === 'k') {
        event.preventDefault()
        const next = idx <= 0 ? 0 : idx - 1
        setFocusedId(items[next]?.id ?? null)
        return
      }

      if (key === 'enter' && focusedId) {
        const item = items.find((i) => i.id === focusedId)
        if (!item) return
        event.preventDefault()
        onOpen(item, {
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey,
        })
        return
      }

      if ((key === 'e' || key === 'delete' || key === 'backspace') && focusedId) {
        const item = items.find((i) => i.id === focusedId)
        if (!item) return
        if (key === 'e' && onArchive) {
          event.preventDefault()
          onArchive(item)
        } else if ((key === 'delete' || key === 'backspace') && onDelete) {
          event.preventDefault()
          onDelete(item)
        }
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [
    containerRef,
    enabled,
    focusedId,
    items,
    onArchive,
    onDelete,
    onOpen,
    setFocusedId,
  ])
}
